// result-verifier-checks.ts
//
// Pure/small-IO checks used by result-verifier.ts, split out to keep both
// files under the 200-line budget. Each function returns a reason string
// (or undefined when the check passes) so the orchestrator can accumulate
// `reasons[]` for the verdict.
//
// Glob/allowed_paths scope checks split further into
// result-verifier-scope-checks.ts — re-exported so callers only import ONE
// module for "result verifier checks".
import { promises as fs } from "node:fs";
import { relative, resolve } from "node:path";
import { EVIDENCE_REQUIRED_ADAPTER_IDS } from "./command-adapter-table.js";
import type { VerificationRecipe } from "./verification-recipe-runner.js";

export {
  checkChangedFilesInScope,
  matchesGlob,
  pathAllowed,
} from "./result-verifier-scope-checks.js";

export function checkRunIdMatches(resultRunId: string, expectedRunId: string): string | undefined {
  if (resultRunId !== expectedRunId) {
    return `run_id trong result (${resultRunId}) không khớp run_id kỳ vọng (${expectedRunId})`;
  }
  return undefined;
}

export function checkTicketRevisionMatches(
  resultRevision: number,
  expectedRevision: number,
): string | undefined {
  if (resultRevision !== expectedRevision) {
    return `ticket_revision trong result (${resultRevision}) không khớp kỳ vọng (${expectedRevision})`;
  }
  return undefined;
}

// D08 (dogfood vòng 4, Bug D fix): `input_hash` is now controller-computed
// and OPTIONAL on the worker's result (autopilot-result-schema.ts). This
// check only fires when the worker actually sent a value — absence is not
// an error (the controller's own `expectedInputHash` is authoritative
// either way), but a value that DOES NOT match is a contract_error-shaped
// signal worth rejecting on, same severity class as a run_id mismatch.
export function checkInputHashMatchesIfPresent(
  resultInputHash: string | undefined,
  expectedInputHash: string,
): string | undefined {
  if (resultInputHash === undefined) return undefined;
  if (resultInputHash !== expectedInputHash) {
    return `input_hash trong result (${resultInputHash}) không khớp input_hash do controller tính (${expectedInputHash})`;
  }
  return undefined;
}

// P21 (layer A): a `review-local-v1` attempt whose ONLY job is to produce a
// review report must actually declare one. P20's real mission (ME-2026-dc845a)
// went `done` having written nothing at all, because no check asked.
//
// `rejected`, not `contract_error`, on purpose: contract_error means "no
// valid result at all" (terminal-result-extractor.ts), while this is a
// perfectly valid result that skipped this adapter's one obligation. The
// distinction is what an operator reads out of the journal, and collapsing
// it would repeat P20's Lỗi A — a real state swallowed into a blander one.
// C2/H1 nửa A (2026-09-16): applies to BOTH review sessions now
// (REVIEW_REPORT_REQUIRED_ADAPTER_IDS) — `review-clean-v1` owes a verdict for
// exactly the same reason, and its playbook already promises rejection. The
// function name keeps its P21 spelling: it is pinned by symbol in
// authority-critical-functions-have-production-callers.test.ts, and renaming a
// guard to widen it reads like a new guard in a diff.
export function checkReviewReportDeclaredForReviewLocal(
  reviewReport: unknown,
  isReviewAttemptOwingReport: boolean,
): string | undefined {
  if (!isReviewAttemptOwingReport) return undefined;
  if (reviewReport !== undefined) return undefined;
  return "review_report vắng trong result của attempt review (review-local-v1 / review-clean-v1) — phiên review phải khai risk + secret_scan trong result (xem workflows/autopilot/review.md, review-clean.md)";
}

// Audit 2026-09-14 finding 2: `AutopilotResultSchema.evidence` is an array with
// no minimum, and `evaluateEvidence` iterates it — so `evidence: []` produced
// zero reasons and the attempt fell through to `done`. A real mission shipped
// six tickets `done` with an implementation returning 999 while the suite
// exited 1, because nothing ever asked for proof.
//
// Not enforceable in the schema: it is shared by all eleven adapters, and
// `plan`/`survey`/`decision`/`review` attempts legitimately run no recipe, so
// `.min(1)` there would contract_error the mission's main path. The verifier is
// the layer that knows WHICH adapter ran — the same argument already recorded
// for `review_report`.
export function checkEvidenceRequiredForAdapter(
  evidence: ReadonlyArray<unknown>,
  commandAdapter: string | undefined,
): string | undefined {
  if (commandAdapter === undefined) return undefined;
  if (!EVIDENCE_REQUIRED_ADAPTER_IDS.includes(commandAdapter)) return undefined;
  if (evidence.length > 0) return undefined;
  return (
    `evidence[] rỗng trong result của adapter "${commandAdapter}" — adapter này chạy ` +
    "verification recipe nên phải khai bằng chứng đã chạy. Không có bằng chứng thì " +
    "không có cơ sở nào để kết luận done (§5.4: verifier là nơi DUY NHẤT quyết định done)."
  );
}

/** `report_path` must resolve strictly inside `stagingDir` — no `..`, no absolute escape. */
export function checkReportPathInStaging(
  reportPath: string,
  stagingDir: string,
): string | undefined {
  if (reportPath.startsWith("/")) {
    return `report_path "${reportPath}" là absolute path — outside staging dir bị cấm`;
  }
  const resolved = resolve(stagingDir, reportPath);
  const rel = relative(stagingDir, resolved);
  if (rel.startsWith("..") || resolve(stagingDir, rel) !== resolved) {
    return `report_path "${reportPath}" nằm ngoài staging dir`;
  }
  return undefined;
}

export async function checkReportFileExists(
  absoluteReportPath: string,
): Promise<string | undefined> {
  try {
    await fs.stat(absoluteReportPath);
    return undefined;
  } catch {
    return `report file missing tại "${absoluteReportPath}"`;
  }
}

export function checkRecipeIsApproved(
  recipeId: string,
  approvedRecipes: VerificationRecipe[],
): string | undefined {
  const approved = approvedRecipes.find((r) => r.recipe_id === recipeId);
  if (approved === undefined) {
    return `recipe "${recipeId}" không có trong verification-profile đã duyệt — worker không được tự thay recipe`;
  }
  return undefined;
}

/** Phase 06d R4 (F-15): the import half alone decides whether the refusal carries the lazy-lookup hint. */
const IMPORT_ONLY_PATTERNS = [
  /cannot find module/i,
  /module not found/i,
  /importerror/i,
  /modulenotfounderror/i,
];
const IMPORT_ERROR_PATTERNS = [...IMPORT_ONLY_PATTERNS, /syntaxerror/i];

/** RED testcase must fail because of MISSING BEHAVIOR, not import/syntax errors. */
export function isImportOrSyntaxErrorOnly(reportText: string): boolean {
  return IMPORT_ERROR_PATTERNS.some((p) => p.test(reportText));
}

/** One of the 4 import patterns (SyntaxError alone ⇒ false, N4). */
export function hasImportErrorMarker(reportText: string): boolean {
  return IMPORT_ONLY_PATTERNS.some((p) => p.test(reportText));
}

const DISABLED_ASSERTION_PATTERNS = [
  /\bit\.skip\s*\(/,
  /\bxit\s*\(/,
  /\bdescribe\.skip\s*\(/,
  /\bit\.only\s*\(/,
  /\btest\.skip\s*\(/,
  /@pytest\.mark\.skip/,
];

export function containsDisabledAssertion(diffOrContent: string): boolean {
  return DISABLED_ASSERTION_PATTERNS.some((p) => p.test(diffOrContent));
}
